import type { HydraulicSystem, Scenarios, OverrideField, Override } from '@shared/hydraulic'
import { isFieldOverridable, upsertOverride, DEFAULT_BASE_SCENARIO_ID } from '@shared/hydraulic'

export type RouteResult =
  | { kind: 'base'; warn: boolean }            // caller mutates Base directly; warn=true if an override was attempted off-allowlist
  | { kind: 'override'; scenarios: Scenarios } // caller writes these scenarios back onto system

/**
 * Decide where an element edit goes. Pure: returns either "mutate Base" or the
 * new `scenarios` with an upserted override. `scenarioId` defaults to the active
 * scenario; the grid passes an explicit column id to edit a non-active node.
 *  - target scenario is Base (or system has no tree) -> Base mutation.
 *  - field is overridable on this element type -> upsert an Override on that node.
 *  - field is NOT overridable (topology/name/etc.) -> Base mutation with warn=true.
 */
export function routeFieldEdit(
  system: HydraulicSystem,
  target: 'vertex' | 'edge',
  id: string,
  field: string,
  value: unknown,
  scenarioId?: string,
): RouteResult {
  const scenarios = system.scenarios
  const targetId = scenarioId ?? scenarios?.active_id ?? DEFAULT_BASE_SCENARIO_ID
  if (!scenarios || targetId === DEFAULT_BASE_SCENARIO_ID) {
    return { kind: 'base', warn: false }
  }
  const elementType = target === 'vertex'
    ? system.vertices.find((v) => v.id === id)?.type
    : system.edges.find((e) => e.id === id)?.type
  if (!elementType || !isFieldOverridable(elementType, field as OverrideField)) {
    return { kind: 'base', warn: true }
  }
  const override = buildOverride(target, id, field as OverrideField, value)
  return { kind: 'override', scenarios: upsertOverride(scenarios, targetId, override) }
}

/** Build the discriminated Override for (target,id,field,value). `pipe_spec`
 * carries both ids; all other fields wrap the value directly. */
function buildOverride(target: 'vertex' | 'edge', id: string, field: OverrideField, value: unknown): Override {
  if (field === 'pipe_spec') {
    const v = value as { pipe_specification_id: string; pipe_size_id: string }
    return { target: 'edge', id, field, value: { pipe_specification_id: v.pipe_specification_id, pipe_size_id: v.pipe_size_id } } as Override
  }
  return { target, id, field, value } as Override
}
